import type {
  WebsiteBlock,
  WebsiteBlueprint,
  WebsiteLink,
  WebsiteLinkDestination,
} from './types'
import { resolveHeaderActions } from './header-actions'

interface LegacyWebsiteLink {
  label: string
  href: string
}

export interface ResolvedWebsiteLink {
  href: string
  target?: '_blank'
  rel?: 'noopener noreferrer'
}

export const websiteLinkDestinationTypes = [
  { id: 'page', name: 'Page' },
  { id: 'external', name: 'External link' },
  { id: 'email', name: 'Email' },
  { id: 'phone', name: 'Phone' },
  { id: 'section', name: 'Page section' },
] as const

export function createWebsiteLinkDestination(type: WebsiteLinkDestination['type']): WebsiteLinkDestination {
  switch (type) {
    case 'page':
      return { type, path: '/' }
    case 'external':
      return { type, url: 'https://', newTab: true }
    case 'email':
      return { type, email: '' }
    case 'phone':
      return { type, phone: '' }
    case 'section':
      return { type, sectionId: '' }
    case 'dynamic':
      return { type, key: '' }
  }
}

export function normalizeWebsiteLink(link: WebsiteLink | LegacyWebsiteLink): WebsiteLink {
  if ('destination' in link) return link

  const href = link.href.trim()

  if (href.startsWith('#')) {
    return { label: link.label, destination: { type: 'section', sectionId: href.slice(1) } }
  }

  if (href.startsWith('mailto:')) {
    return { label: link.label, destination: { type: 'email', email: href.slice(7) } }
  }

  if (href.startsWith('tel:')) {
    return { label: link.label, destination: { type: 'phone', phone: href.slice(4) } }
  }

  if (href.startsWith('/')) {
    return { label: link.label, destination: { type: 'page', path: safeInternalPath(href) } }
  }

  return { label: link.label, destination: { type: 'external', url: href, newTab: true } }
}

export function normalizeWebsiteBlueprintLinks(blueprint: WebsiteBlueprint): WebsiteBlueprint {
  const normalized = structuredClone(blueprint)

  for (const navigation of normalized.navigations ?? []) {
    normalizeNavigationItems(navigation.items, normalized.pages)
  }

  for (const page of normalized.pages) {
    for (const block of page.blocks) normalizeBlockLinks(block, normalized.pages)
  }

  return normalized
}

export function resolveWebsiteLink(link: WebsiteLink | LegacyWebsiteLink): ResolvedWebsiteLink {
  const normalized = normalizeWebsiteLink(link)
  const destination = normalized.destination

  switch (destination.type) {
    case 'page':
      return { href: safeInternalPath(destination.path) }
    case 'external': {
      const href = safeExternalUrl(destination.url)
      return destination.newTab && href !== '#'
        ? { href, target: '_blank', rel: 'noopener noreferrer' }
        : { href }
    }
    case 'email': {
      const email = destination.email.trim().replace(/^mailto:/i, '').split(/[?&]/, 1)[0] ?? ''
      const subject = destination.subject?.trim()
      return {
        href: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
          ? '#'
          : `mailto:${email}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`,
      }
    }
    case 'phone': {
      const phone = destination.phone.replace(/^tel:/i, '').replace(/[^+\d,;#*]/g, '')
      return { href: phone === '' ? '#' : `tel:${phone}` }
    }
    case 'section': {
      const sectionId = destination.sectionId.trim().replace(/^#/, '').replace(/[^a-zA-Z0-9_-]/g, '-')
      const path = destination.path ? safeInternalPath(destination.path) : ''
      return { href: sectionId === '' ? (path || '#') : `${path}#${sectionId}` }
    }
    case 'dynamic':
      return { href: '#' }
  }
}

function normalizeBlockLinks(block: WebsiteBlock, pages: WebsiteBlueprint['pages']): void {
  const bind = (link: WebsiteLink): WebsiteLink => bindPageReference(normalizeWebsiteLink(link), pages)

  if (block.type === 'header') {
    block.content.brandLink = bindPageReference(block.content.brandLink
      ? normalizeWebsiteLink(block.content.brandLink)
      : {
          label: block.content.businessName,
          destination: { type: 'page', path: '/' },
        }, pages)
    if (block.content.navigation) {
      block.content.navigation = block.content.navigation.map((link) => bindPageReference(normalizeWebsiteLink(link), pages))
    }
    const legacyContent = block.content as typeof block.content & { callToAction?: WebsiteLink }
    block.content.actions = resolveHeaderActions(block.content)
      .map((action) => ({
        ...action,
        link: bindPageReference(normalizeWebsiteLink(action.link), pages),
      }))
    delete legacyContent.callToAction
  }

  if (block.type === 'hero') {
    block.content.primaryAction = bind(block.content.primaryAction)
    if (block.content.secondaryAction) {
      block.content.secondaryAction = bind(block.content.secondaryAction)
    }
  }

  if (block.type === 'services') {
    if (block.content.action) block.content.action = bind(block.content.action)
    for (const item of block.content.items) {
      if (item.action) item.action = bind(item.action)
    }
  }

  if (block.type === 'features' && block.content.action) {
    block.content.action = bind(block.content.action)
  }

  if (block.type === 'about' && block.content.action) {
    block.content.action = bind(block.content.action)
  }

  if (block.type === 'faq' && block.content.action) {
    block.content.action = bind(block.content.action)
  }

  if (block.type === 'cta') {
    block.content.primaryAction = bind(block.content.primaryAction)
    if (block.content.secondaryAction) block.content.secondaryAction = bind(block.content.secondaryAction)
  }

  if (block.type === 'contact') {
    block.content.methods = block.content.methods.map((method) => ({ ...method, link: bind(method.link) }))
    if (block.content.primaryAction) block.content.primaryAction = bind(block.content.primaryAction)
  }

  if (block.type === 'footer') {
    block.content.brandLink = bind(block.content.brandLink ?? {
      label: block.content.businessName,
      destination: { type: 'page', path: '/' },
    })
    block.content.columns = block.content.columns.map((column) => ({
      ...column,
      links: column.links.map(bind),
    }))
    if (block.content.socialLinks) block.content.socialLinks = block.content.socialLinks.map(bind)
  }
}

function normalizeNavigationItems(
  items: NonNullable<WebsiteBlueprint['navigations']>[number]['items'],
  pages: WebsiteBlueprint['pages'],
): void {
  for (const item of items) {
    if (item.destination) {
      const link = bindPageReference(normalizeWebsiteLink({
        label: item.label,
        destination: item.destination,
      }), pages)
      item.destination = link.destination
    }

    normalizeNavigationItems(item.children, pages)
  }
}

function bindPageReference(link: WebsiteLink, pages: WebsiteBlueprint['pages']): WebsiteLink {
  const destination = link.destination

  if ((destination.type === 'page' || destination.type === 'section') && destination.path && !destination.pageId) {
    destination.pageId = pages.find((page) => page.slug === destination.path)?.id
  }

  return link
}

function safeInternalPath(path: string): string {
  const normalized = path.trim()
  if (normalized === '' || normalized === '/') return '/'
  if (!normalized.startsWith('/') || normalized.startsWith('//')) return '/'

  return normalized
}

function safeExternalUrl(url: string): string {
  const normalized = url.trim()

  try {
    const parsed = new URL(normalized)
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.toString() : '#'
  } catch {
    return '#'
  }
}
